import type { HTMLProps } from 'react';
import { useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useForm } from 'react-hook-form';

import { Button, Form, Input, NotificationBox } from 'src/components';
import { userHelpers } from 'src/utils/userHelpers';

import { Trans, useLingui } from '@lingui/react/macro';
import { NoUserError } from 'src/utils/noUserError';
import {
  useAccountInfo,
  useUpdateAccountInfo,
} from '../../../../api/queries/account';

interface NameForm {
  firstName: string;
  lastName: string;
}

export const EditNameForm = (props: HTMLProps<HTMLDivElement>) => {
  const { user } = useAuth0();
  const { t } = useLingui();
  const accountInfo = useAccountInfo();

  const updateAccountInfo = useUpdateAccountInfo();
  const [isEditable, setIsEditable] = useState(false);

  const form = useForm<NameForm>({
    defaultValues: {
      firstName: user?.given_name ?? '',
      lastName: user?.family_name ?? '',
    },
  });

  const onSubmit = (data: NameForm) => {
    updateAccountInfo.mutate(data, {
      onSuccess: () => {
        setIsEditable(false);
      },
    });
  };

  if (!user) {
    throw new NoUserError('No user found when loading edit name form');
  }

  const identities = accountInfo.data?.identities ?? [];

  const allowedToEdit = userHelpers.canEditName(user, identities);

  return (
    <div {...props}>
      <Form onSubmit={onSubmit} {...form}>
        <div className="flex w-full flex-col justify-between gap-16 lm:flex-row">
          <div className="flex w-full flex-col gap-16 ls:flex-row">
            <Input
              label={t`First name`}
              name="firstName"
              data-cy="first-name"
              className="w-full flex-1 ls:w-fit"
              focusOnIsEditable
              required
              isEditable={isEditable}
              maxLength={150}
            />
            <Input
              className="w-full flex-1 ls:w-fit"
              label={t`Last name`}
              name="lastName"
              data-cy="last-name"
              required
              isEditable={isEditable}
              maxLength={150}
            />
          </div>
          {!isEditable && allowedToEdit && (
            <Button
              data-cy="edit-name-button"
              variant="tertiary"
              className="w-full self-start ls:w-fit"
              size="medium"
              type="button"
              onClick={() => setIsEditable(true)}
            >
              <Trans>Edit</Trans>
            </Button>
          )}
        </div>

        {isEditable && (
          <div className="my-16 flex gap-16">
            <Button
              type="button"
              onClick={() => {
                setIsEditable(false);
                if (accountInfo.data) {
                  form.reset(accountInfo.data);
                } else {
                  form.reset();
                }
                updateAccountInfo.reset();
              }}
              variant="secondary"
              data-cy="cancel-edit-name-button"
              size="medium"
            >
              <Trans>Cancel</Trans>
            </Button>

            <Button
              type="submit"
              disabled={updateAccountInfo.isPending}
              loading={updateAccountInfo.isPending}
              data-cy="save-name-button"
              size="medium"
              variant="primary"
            >
              <Trans>Save</Trans>
            </Button>
          </div>
        )}

        {updateAccountInfo.isError && (
          <NotificationBox
            error={updateAccountInfo.error}
            data-cy="edit-name-error"
          />
        )}
      </Form>
    </div>
  );
};
